import {
  ChartResultStatus,
  type ChartFetchResult,
} from '../catalog/types';
import { IDLE_CHART_RESULT } from '../hooks/chartResultUtils';
import type { ChartRuntime } from 'views/analytics/types';
import type { UserInDto } from 'shared/domain/user/types/inDto';
import type { ChartFiltersUnion } from 'shared/types/analytics';
import { isFunction } from 'shared/utils/function';
import { debugLog } from 'shared/logger/debugLog';

function hasRuntimeAccess(
  runtime: ChartRuntime<ChartFiltersUnion>,
  user: UserInDto
): boolean {
  const { canAccess } = runtime;

  if (!isFunction(canAccess)) {
    return false;
  }

  try {
    return canAccess(user) ?? false;
  } catch (error: unknown) {
    debugLog('Chart access check failed', error);
    return false;
  }
}

export function chartRuntimeToChartResult(
  runtime: ChartRuntime<ChartFiltersUnion>,
  user: UserInDto
): ChartFetchResult {
  const reload = runtime.forceUpdate;

  if (!hasRuntimeAccess(runtime, user)) {
    return { ...IDLE_CHART_RESULT, reload };
  }

  // A chart read again (a refresh, other filters) keeps showing its data until the new one comes:
  // a spinner in its place and the mount animation after it flashed the charts (PT-6804).
  const hasData = runtime.updatedAt() > 0;
  if (!runtime.filters || (runtime.loading && !hasData)) {
    return {
      status: ChartResultStatus.loading,
      rawData: null,
      filters: runtime.filters,
      dataGeneratedAt: null,
      error: null,
      reload,
    };
  }

  return {
    status: ChartResultStatus.success,
    rawData: runtime.rawData,
    filters: runtime.filters,
    dataGeneratedAt: runtime.updatedAt(),
    error: null,
    reload,
  };
}
